import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, UploadCloud, AlertTriangle, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * V1.42.8 · Vista previa post-recorte antes de sustituir en OneDrive.
 * Descarga el JPEG del endpoint `previewUrl` y muestra la imagen final. Al
 * confirmar, ejecuta `onConfirm` (que hará el POST a apply-crop-to-onedrive).
 *
 * Props:
 *   open, onClose
 *   title           · texto del header (ej: "Vista previa · sustituir en OneDrive")
 *   previewUrl      · endpoint GET que devuelve image/jpeg
 *   hasCrop         · true si el archivo tiene crop_config (afecta al mensaje)
 *   onConfirm       · async () => void (llama al apply-crop-to-onedrive)
 */
export default function CropPreviewDialog({ open, onClose, title, previewUrl, hasCrop, onConfirm }) {
  const [loading, setLoading] = useState(true);
  const [imgUrl, setImgUrl] = useState("");
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open || !previewUrl) return;
    let alive = true;
    let blobUrl = "";
    setLoading(true);
    setError("");
    setImgUrl("");
    (async () => {
      try {
        const res = await api.get(previewUrl, { responseType: "blob" });
        if (!alive) return;
        blobUrl = URL.createObjectURL(res.data);
        setImgUrl(blobUrl);
      } catch (err) {
        if (!alive) return;
        setError(formatApiError(err?.response?.data?.detail) || "No se pudo generar la vista previa");
      } finally { if (alive) setLoading(false); }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [open, previewUrl]);

  const submit = async () => {
    setConfirming(true);
    try {
      await onConfirm();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo sustituir en OneDrive");
    } finally { setConfirming(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="crop-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ImageIcon className="h-4 w-4 text-indigo-600" />
            {title || "Vista previa · sustituir en OneDrive"}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-xl bg-neutral-100 border border-neutral-200 overflow-hidden flex items-center justify-center min-h-[280px] max-h-[65vh]">
          {loading ? (
            <div className="flex flex-col items-center gap-2 py-8 text-neutral-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-xs">Generando vista previa…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 py-8 text-red-600 px-4 text-center">
              <AlertTriangle className="h-5 w-5" />
              <span className="text-xs font-semibold">{error}</span>
            </div>
          ) : (
            <img
              src={imgUrl}
              alt="Vista previa recortada"
              className="max-w-full max-h-[65vh] object-contain bg-white"
              data-testid="crop-preview-image"
            />
          )}
        </div>

        <div className="text-xs text-neutral-600 leading-relaxed px-1">
          {hasCrop ? (
            <>
              Vas a <strong>sustituir el archivo original en OneDrive</strong> por esta versión recortada.
              Esta acción es <strong>irreversible</strong>: el archivo actual quedará sobrescrito.
            </>
          ) : (
            <>
              No hay recorte configurado. Se re-subirá el archivo tal cual está ahora en OneDrive
              (útil para forzar la validación completa del ciclo).
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 rounded-lg border border-neutral-300 hover:bg-neutral-50 text-xs font-semibold"
            data-testid="crop-preview-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={loading || !!error || confirming}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-60"
            data-testid="crop-preview-confirm"
          >
            {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
            Confirmar y subir a OneDrive
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
